import { useState, useContext, FormEvent, ChangeEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import ThemeContext from './user/ThemeContext'
import logoImage from '@/assets/logo-beta.png'

interface PatentLandingPageProps {
  initialError?: string
  failedPatentNumber?: string
}

const PatentLandingPage = ({ initialError = '', failedPatentNumber = '' }: PatentLandingPageProps) => {
  const navigate = useNavigate()
  const { theme } = useContext(ThemeContext)
  const [patentNumber, setPatentNumber] = useState('')
  const [error, setError] = useState(initialError)

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Validate input
    const trimmedNumber = patentNumber.trim()
    if (!trimmedNumber) {
      setError('Please enter a patent or publication number')
      return
    }

    // Remove any spaces, dashes, commas
    const cleanedNumber = trimmedNumber.replace(/[\s,\-]/g, '')

    // Normalize to uppercase (country code and kind code)
    const normalizedNumber = cleanedNumber.replace(
      /^([a-z]{2})(\d+)([a-z]\d*)?$/i,
      (_match, country: string, numberPart: string, kind?: string) => {
        return country.toUpperCase() + numberPart + (kind ? kind.toUpperCase() : '')
      }
    )

    // Navigate to the patent page
    navigate(`/${normalizedNumber}`)
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPatentNumber(e.target.value)
    if (error) setError('')
  }

  return (
    <div className={`min-h-screen flex items-center justify-center py-4 ${
      theme === 'dark' ? 'bg-gray-900' : 'bg-[#548698]'
    }`}>
      <div className={`max-w-2xl w-full mx-4 p-6 rounded-lg shadow-xl ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
      }`}>
        {/* Error Banner */}
        {initialError && (
          <div className="mb-5 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border-2 border-red-500">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-red-800">
                  Failed to Load Patent
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  {failedPatentNumber && (
                    <p className="font-semibold mb-1">Patent Number: {failedPatentNumber}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="text-center mb-5">
          {/* Logo */}
          <div className="flex justify-center mb-3">
            <a href="/" className="cursor-pointer">
              <img
                src={logoImage}
                alt="Patent Loop Logo"
                className="h-36 w-auto hover:opacity-80 transition-opacity"
              />
            </a>
          </div>

          <h1 className={`text-3xl font-bold mb-2 flex items-center justify-center gap-2.5 ${
            theme === 'dark' ? 'text-primary-light-3' : 'text-primary'
          }`}>
            Patent Lookup
            <span className={`text-xs font-bold px-2.5 py-0.5 mt-2 rounded-full ${
              theme === 'dark'
                ? 'bg-gradient-to-r from-secondary-light to-secondary text-gray-900'
                : 'bg-gradient-to-r from-green-600 to-green-200 text-white'
            } shadow-md`}>
              FREE
            </span>
          </h1>
          <p className={`text-base ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Enter a patent or publication number to view its specification
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="patentNumber"
              className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
              }`}
            >
              Patent/Publication Number
            </label>
            <input
              id="patentNumber"
              type="text"
              value={patentNumber}
              onChange={handleInputChange}
              placeholder="e.g., US10123456, 10123456, US20210123456A1"
              className={`w-full px-4 py-2.5 text-base rounded-lg border focus:outline-none focus:ring-2 transition ${
                error
                  ? 'border-red-500 focus:ring-red-500'
                  : theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white focus:ring-primary-light-2'
                    : 'bg-white border-gray-300 text-gray-900 focus:ring-primary'
              }`}
              autoFocus
            />
          </div>

          <button
            type="submit"
            className={`w-full px-6 py-2.5 text-base font-semibold rounded-lg shadow-md transition ${
              theme === 'dark'
                ? 'bg-primary-light-2 hover:bg-primary-light-1 text-gray-900'
                : 'bg-primary hover:bg-primary-mid text-white'
            }`}
          >
            View Patent / Publication
          </button>
        </form>

        <div className={`mt-5 p-3 rounded-lg ${
          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
        }`}>
          <h3 className={`text-xs font-semibold mb-1.5 ${
            theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
          }`}>
            Supported Formats:
          </h3>
          <ul className={`text-xs space-y-0.5 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            <li>• US Patents: <span className="font-mono">US10123456</span> or <span className="font-mono">10123456</span></li>
            <li>• US Publications: <span className="font-mono">US20210123456A1</span></li>
            <li>• International: <span className="font-mono">EP2234567A1</span>, <span className="font-mono">WO2020123456</span></li>
          </ul>
        </div>

        {/* Login/Sign-up CTA */}
        <div className={`mt-4 p-4 rounded-lg border-2 ${
          theme === 'dark'
            ? 'bg-gradient-to-br from-primary-dark to-gray-800 border-primary-light-2'
            : 'bg-gradient-to-br from-primary-light-4 to-white border-primary'
        }`}>
          <div className="text-center">
            <h3 className={`text-lg font-bold mb-1.5 ${
              theme === 'dark' ? 'text-primary-light-3' : 'text-primary-dark'
            }`}>
              Unlock Advanced Patent Tools
            </h3>
            <p className={`text-xs mb-3 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Get access to 24+ AI-powered patent analysis tools including claims support mapping,
              specification analysis, office action processing, IDS generation, and much more.
            </p>
            <div className="flex flex-col items-center sm:flex-row gap-2 justify-center">
              <a
                href="/"
                className={`px-5 py-[10px] rounded-lg font-semibold text-sm shadow-md transition transform hover:scale-105 ${
                  theme === 'dark'
                    ? 'bg-primary-light-2 hover:bg-primary-light-1 text-gray-900'
                    : 'bg-primary hover:bg-primary-mid text-white'
                }`}
              >
                Sign Up Free
              </a>
              <a
                href="/"
                className={`px-5 py-2 rounded-lg font-semibold text-sm border-2 transition transform hover:scale-105 ${
                  theme === 'dark'
                    ? 'border-primary-light-2 text-primary-light-2 hover:bg-primary-light-2 hover:text-gray-900'
                    : 'border-primary text-primary hover:bg-primary hover:text-white'
                }`}
              >
                Login
              </a>
            </div>
            <p className={`text-xs mt-2 font-bold ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`}>
              ✨ Free plan includes 50 credits • No credit card required
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PatentLandingPage
